import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HISTORICAL_STATUSES = new Set(["graphics_complete", "graphics_completed", "approved"]);

const STATUS_OPTIONS = [
  ["new_project", "New Project"],
  ["graphic_team_review", "Graphic Team Review"],
  ["artwork_submitted", "Artwork Submitted"],
  ["pdf_proof_phase", "PDF Proof Phase"],
  ["swatch_drawdown_phase", "Swatch Drawdown"],
  ["pilot_proof_phase", "Pilot Proof Phase"],
  ["graphics_completed", "Graphics Completed"],
];

export const ProjectStatusQuickControl = ({ projectId }) => {
  const { canChangeProjectStatus } = useAuth();
  const [project, setProject] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!projectId || !canChangeProjectStatus) return;
    axios.get(`${API}/projects/${projectId}`)
      .then((response) => setProject(response.data))
      .catch(() => setProject(null));
  }, [projectId, canChangeProjectStatus]);

  if (!canChangeProjectStatus || !project || HISTORICAL_STATUSES.has(project.status)) return null;

  const changeStatus = async (status) => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/projects/${projectId}/status`, { status });
      setProject(response.data);
      toast.success("Project status updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update project status");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3">
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Project Access</p>
        <p className="text-sm font-medium">You can update the workflow status for this assigned project.</p>
      </div>
      <div className="flex items-center gap-2">
        {saving && <RefreshCw size={16} className="animate-spin text-muted-foreground" />}
        <select
          value={project.status || "new_project"}
          onChange={(event) => changeStatus(event.target.value)}
          disabled={saving}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
        >
          {STATUS_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>
    </div>
  );
};
